<template>
  <WdsStack justify-space-between>
    <div>
      <WdsHeading type="title4">{{ $tc(':count group | :count groups', items.length) }}</WdsHeading>
    </div>
    <div>
      <WdsButton data-test="create-group-button" primary @click="showCreate = true">
        {{ $t('Create group') }}
      </WdsButton>
    </div>
  </WdsStack>
  <div class="tw-mt-5 tw-w-60">
    <WdsInput v-model="search" :placeholder="$t('Search name, date...')">
      <template #prefix>
        <WdsSpinner v-if="loading" indeterminate />
        <WdsIcon v-else name="search" :size="16" />
      </template>
    </WdsInput>
  </div>
  <BaseDataTable slim :columns="columns" :entries="items" :loading="groupsLoading">
    <template #Name="{ row }">
      <div class="tw-max-w-[300px]">
        <WdsTooltip v-if="row.description !== ''" :text="row.description" global>
          {{ row.name }}
        </WdsTooltip>
        <span v-else>
          {{ row.name }}
        </span>
      </div>
    </template>
    <template #Group="{ row }">
      <WdsStack justify-space-between align-center>
        <WdsStack gap-sm align-center>
          <WdsTooltip :text="$t('Number of people assigned to this group')" global>
            <WdsText class="tw-mr-3">{{ row?.users.paginatorInfo.total ?? 0 }}</WdsText>
          </WdsTooltip>
          <WdsTooltip v-for="member of row?.users.data" :key="member.name" :text="member.name">
            <WdsAvatar
              :initials="member.initials"
              :path="getAvatarPath(member.avatar, member.id)"
              sm
            />
          </WdsTooltip>
          <WdsAvatar
            v-if="row?.users.paginatorInfo.hasMorePages"
            :initials="`+${row?.users.paginatorInfo.total - row?.users.paginatorInfo.count}`"
            sm
          />
        </WdsStack>
      </WdsStack>
    </template>
    <template #Status="{ row }">
      <WdsLabel v-if="row.status === 'INACTIVE'" error sm>{{ $t('Inactive') }}</WdsLabel>
      <WdsLabel v-else-if="row.status === 'ARCHIVED'" warning sm>{{ $t('Archived') }}</WdsLabel>
      <WdsLabel v-else success sm>{{ $t('Active') }}</WdsLabel>
    </template>
    <template #Settings="{ row }">
      <WdsStack justify-end gap-sm align-center class="tw-w-full">
        <WdsButtonGhost @click="editGroup(row)">{{ $t('Edit') }}</WdsButtonGhost>
        <WdsSpinner v-if="deleteLoading && activeDeletionIds[row.id]" indeterminate />
        <WdsButtonIcon
          v-else
          sm
          transparent-background
          class="muted-button-icon"
          @click="startDeleteGroup(row)"
        >
          <WdsIcon name="trash-can" :size="16" />
        </WdsButtonIcon>
      </WdsStack>
    </template>
  </BaseDataTable>
  <WdsModal
    :title="$t(showEdit ? 'Edit group' : 'Create group')"
    :show="show"
    @cancel="modalClosed"
  >
    <ApprovalGroupEditor :group="group" @cancel="modalClosed" @submitted="submitted" />
  </WdsModal>
  <GroupDeleteModal
    :show="showDeleteModal"
    :group-name="activeDeletionName"
    @cancel="endDeleteGroup"
    @submit="deleteGroup"
  />
</template>

<script lang="ts" setup>
import { computed, Ref, ref } from 'vue'
import {
  WdsHeading,
  WdsButtonGhost,
  WdsModal,
  WdsButtonIcon,
  WdsButton,
  WdsStack,
  WdsText,
  WdsIcon,
  WdsAvatar,
  WdsTooltip,
  WdsInput,
  WdsSpinner,
  WdsLabel,
} from '@worksome/wds-components'
import { useMutation, useQuery } from '@vue/apollo-composable'
import BaseDataTable from '@/components/BaseDataTable'
import { $tc, $t } from '@/app-bootstrapping/SetUpInternationalization'
import ApprovalGroupEditor from '@/components/Workflows/Pages/Groups/GroupEditor.vue'
import { userGroupsQuery } from '@/components/Workflows/Pages/Groups/graphql/queries'
import { deleteUserGroupMutation } from '@/components/Workflows/Pages/Groups/graphql/mutations'
import { NetworkError } from '@/utils/notification'
import { getAvatarPath } from '@/utils/avatar'
import GroupDeleteModal from '@/components/Workflows/Pages/Groups/GroupDeleteModal.vue'
import { UserGroup } from '@/components/Workflows/Pages/Workflow/workflow'
import { UserGroupStatus } from '@/gql/graphql'

const clientId = 'publicApiCustomFields'

const maxGroupMembersToDisplay = 10
const loading = ref(false)
const showCreate = ref(false)
const search = ref('')

const showEdit = ref(false)
const group = ref(null)

const show = computed({
  get() {
    return showCreate.value || showEdit.value
  },
  set() {
    showCreate.value = false
    showEdit.value = false
  },
})

const {
  result,
  loading: groupsLoading,
  refetch,
} = useQuery(
  userGroupsQuery,
  () => ({
    accounts: [currentAccount.globalId],
    search: search.value,
    groupFirst: 50,
    groupPage: 1,
    usersFirst: maxGroupMembersToDisplay,
    usersPage: 1,
  }),
  () => ({
    clientId,
  }),
)

const columns = ref([
  {
    key: 'name',
    title: $t('Group name'),
    slot: 'Name',
  },
  {
    key: 'group',
    title: $t('Group members'),
    slot: 'Group',
  },
  {
    key: 'status',
    title: $t('Status'),
    slot: 'Status',
  },
  {
    key: 'settings',
    title: '',
    slot: 'Settings',
  },
])

const items = computed(() => result.value?.userGroups.data ?? [])

const modalClosed = () => {
  show.value = false
  group.value = null
}

const submitted = (response) => {
  if (
    showCreate.value ||
    (response?.data?.updateUserGroup?.status &&
      response.data.updateUserGroup.status === UserGroupStatus.Archived)
  ) {
    refetch()
  }
  modalClosed()
}

const editGroup = (groupRow) => {
  group.value = groupRow
  showEdit.value = true
}

const {
  mutate: deleteGroupMutation,
  loading: deleteLoading,
  onError: onUserGroupDeleteError,
} = useMutation(deleteUserGroupMutation, () => ({
  clientId,
  update(cache, { data }) {
    if (data?.deleteUserGroup) {
      cache.evict({ id: cache.identify(data.deleteUserGroup) })
      cache.gc()
    }
  },
}))

const activeDeletionIds = ref({})
const showDeleteModal = ref(false)

type UserGroupHolder = Partial<UserGroup>
const activeDeletionGroup: Ref<UserGroupHolder> = ref({})
const activeDeletionName = computed(() => activeDeletionGroup.value.name ?? '')

const startDeleteGroup = (groupRow: UserGroup) => {
  activeDeletionGroup.value = groupRow
  showDeleteModal.value = true
}

const endDeleteGroup = () => {
  showDeleteModal.value = false
  activeDeletionGroup.value = {}
}

const deleteGroup = async () => {
  const { id } = activeDeletionGroup.value

  if (id === undefined) return

  activeDeletionIds.value[id] = true

  endDeleteGroup()

  await deleteGroupMutation({ input: { id } })

  delete activeDeletionIds.value[id]

  refetch()
}

onUserGroupDeleteError(({ graphQLErrors, networkError }) => {
  const { name } = activeDeletionGroup.value

  let ex: any = null
  let errorMessage = $t('Something went wrong when trying to delete :group', { group: name })

  if (graphQLErrors) {
    ex = graphQLErrors
    errorMessage = graphQLErrors[0].message
  } else if (networkError) {
    ex = networkError
    errorMessage = networkError.message
  }

  new NetworkError({
    ex,
    msg: errorMessage,
  }).dispatch()
})
</script>

<style lang="scss" scoped>
button.muted-button-icon {
  color: #b2b2be;
  &:hover {
    color: inherit;
  }
}
</style>
